import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { Box, DialogContent, DialogActions, Button, Dialog, Grid, Divider } from '@mui/material';
import { DialogTitleWithClose, Loader } from 'ui';
import { Field, Form, Formik } from 'formik';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { PortfolioItem, SelectedEntities } from 'shared/types/portfolios';
import { useSnackbar } from 'shared/hooks/useSnackbar';
import { theme } from 'theme';
import { PortfolioAction } from 'modules/common/PortfolioManager/types';
import { useFetchPortfolios } from '../../GraphQL/portfolios/hooks';
import { getResponseMessage } from '../../GraphQL/utils';
import { SearchControl } from './SearchControl';
import { PortfoliosSelectControl } from './PortfoliosSelectControl';
import { useVariantAction } from '../portfolioModalUtils';
import { debounce } from 'lodash';

const useStyles = makeStyles({
  dialog: {
    borderRadius: theme.spacing(1.5),
    overflow: 'hidden',
    width: theme.spacing(63),
  },
  searchField: {
    marginTop: theme.spacing(1),
  },
  content: {
    paddingTop: 'unset',
    paddingBottom: 'unset',
    height: theme.spacing(37),
    // @ts-ignore
    overflowY: 'overlay',
  },
  list: {
    padding: 'unset',
  },
  listItem: {
    margin: 'unset',
    paddingLeft: 'unset',
    paddingRight: 'unset',
  },
});

type FormData = {
  portfolioId: string;
  search: string;
};

type UpdateEntitiesModalProps = {
  onClose?: () => void;
  onClickCreateNew?: () => void;
  onActionSuccess?: (refresh: boolean, reason: string) => void;
  open?: boolean;
  itemsData?: SelectedEntities;
  ratingType?: ratingTypes;
  selectedAll?: boolean;
  sourcePortfolioId?: string;
  variant?: PortfolioAction;
  itemType: PortfolioItem;
};

export const UpdateEntitiesModal: FC<UpdateEntitiesModalProps> = ({
  onClose = () => null,
  onClickCreateNew = () => null,
  onActionSuccess = null,
  open = false,
  itemsData = {
    count: 0,
    itemIds: {},
    items: {},
    type: null,
  },
  variant = 'add',
  itemType,
  sourcePortfolioId = '',
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [updating, setUpdating] = useState(false);
  const [searchVal, setSearchVal] = useState<string>();
  const [nameFilter, setNameFilter] = useState<string>();
  const { error, success } = useSnackbar();
  const { data, loading, refetch } = useFetchPortfolios(
    0,
    20,
    undefined,
    nameFilter
      ? {
          name: nameFilter,
        }
      : undefined,
  );

  const portfolios = data?.portfolios.filter((item) => !item.isDedicated) || [];
  const isPending = loading || updating;

  useEffect(() => {
    if (open) {
      setUpdating(true);
      refetch().finally(() => setUpdating(false));
    }
  }, [refetch, open]);

  const { title, submit, refreshSettings, action } = useVariantAction(
    itemsData,
    itemType,
    sourcePortfolioId,
  )[variant];

  const handleSubmit = ({ portfolioId }: FormData) => {
    setUpdating(true);

    action(portfolioId)
      .then(({ data: responseData }) => {
        if (onActionSuccess) {
          onActionSuccess(refreshSettings.refresh, refreshSettings.reason);
        }

        success(getResponseMessage(responseData, t('SUCCESS')));
      })
      .catch(({ networkError }) => {
        const { data: responseData, errors } = networkError?.result as {
          data: any;
          errors: { message: string }[];
        };

        error(
          getResponseMessage(
            responseData,
            errors ? errors.map((item) => item.message).join('<br />') : t('UNEXPECTED_ERROR'),
          ),
        );
      })
      .finally(() => {
        setUpdating(false);
        onClose();
      });
  };

  const handleCreateNewPortfolio = () => {
    onClickCreateNew();
    onClose();
  };

  useEffect(() => {
    debounce(setNameFilter, 500)(searchVal);
  }, [searchVal]);

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.dialog }}>
      <Formik
        initialValues={{ portfolioId: '', search: searchVal } as FormData}
        onSubmit={handleSubmit}
      >
        <Form>
          <DialogTitleWithClose onClose={onClose}>{title}</DialogTitleWithClose>
          <Box px={3}>
            <Divider />
            <Field
              name="search"
              placeholder="Search portfolio name"
              className={classes.searchField}
              component={SearchControl}
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
            />
            <Divider />
          </Box>
          <DialogContent className={classes.content}>
            <Field
              name="portfolioId"
              className={classes.list}
              portfolios={portfolios}
              search={searchVal}
              component={PortfoliosSelectControl}
            />
          </DialogContent>
          <DialogActions>
            <Grid container spacing={2}>
              <Grid item xs>
                <Button onClick={onClose}>{t('CANCEL')}</Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" onClick={handleCreateNewPortfolio}>
                  {t('TOOLBAR.CREATE_NEW_PORTFOLIO')}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isPending || !portfolios.length}
                >
                  {submit}
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </Form>
      </Formik>
      {isPending && <Loader cover />}
    </Dialog>
  );
};
